import { isClientSide } from '../ssrUtils';

/**
 * @name hasQueryParams
 * @description Search if an url already have query params
 * @param {string} url
 * @returns {boolean}
 */
export const hasQueryParams = (url: string): boolean => url.includes('?');

/**
 * @name injectQueryParamToUrl
 * @description Takes an url as input and returns the url with query parameters injected
 * This function checks if the url already have query params
 * @param {string} key - The key of the query
 * @param {string} value - The value of the query
 * @param {string} url - The url to mutate
 */

type injectQueryParamToUrlType = (args: {
  key: string;
  value: any;
  url: string;
}) => string;

export const injectQueryParamToUrl: injectQueryParamToUrlType = ({
  key,
  value,
  url,
}: {
  key: string;
  value: string;
  url: string;
}) => {
  const separator = hasQueryParams(url) ? '&' : '?';

  return `${url}${separator}${key}${value ? `=${value}` : ''}`;
};

export function buildUrlWithParameters(url: string, parameters: Object) {
  if (!url) return '';

  if (!parameters) return url;

  const parametersToArray = Object.entries(parameters);
  const reducer = (urlAccumulated: string, [key, value]: [string, string]) =>
    injectQueryParamToUrl({ key, value, url: urlAccumulated });

  const urlWithParameters = parametersToArray.reduce(reducer, url);

  return urlWithParameters;
}

export const getParametersFromUrl = (
  url: string,
): { [string: string]: string } => {
  if (!url) return {};

  const index = url.indexOf('?');

  if (index === -1) return {};

  const params = url.slice(index + 1).trim();
  if (!params) {
    return {};
  }

  const reducer = (
    accumulator: { [s: string]: string },
    currentParam: string,
  ) => {
    const [key, value] = currentParam.split('=');
    return { ...accumulator, [key]: value };
  };

  return params.split('&').reduce(reducer, {});
};

export const getSourceFromUrlParameters = (url: string): string =>
  getParametersFromUrl(url).source || '';

export const isUrlInternal = (url: string) => {
  if (!url) return false;
  if (
    (isClientSide && url.startsWith(`${window.location.origin}/pdf/`)) ||
    url.startsWith('https://www.doctrine.fr/pdf/') ||
    url.startsWith('/pdf/')
  )
    return false;
  return (
    (isClientSide && url.startsWith(window.location.origin)) ||
    url.startsWith('https://www.doctrine.fr') ||
    (url.startsWith('/') && !url.startsWith('/redirect_to_comment'))
  );
};

export const cleanUrlFromParameters = () => {
  window.history.replaceState(
    {},
    '',
    `${window.location.origin}${window.location.pathname}`,
  );
};

export const rebuildUrlWithoutSource = () => {
  const params = getParametersFromUrl(window.location.search);
  const hash = window.location.hash || '';
  const url = `${window.location.origin}${window.location.pathname}`;
  if (params.source) {
    delete params.source;
  }
  if (params.utm_source) {
    delete params.utm_source;
  }
  if (params.utm_medium) {
    delete params.utm_medium;
  }
  window.history.replaceState(
    {},
    '',
    `${buildUrlWithParameters(url, params)}${hash}`,
  );
};
